/**
 * Maps a JSON data-source cell to the text its input shows and back to the value saved.
 *
 * A row may hold structured values (e.g. an attributed `linkGeoItems` array), which the importer
 * reads as a native structure. Saved as text instead, pass-through types would keep a string where a
 * structure belongs, and a malformed `linkGeoItems` cell would fail the import run. So an edited
 * structured cell is saved only as the array or object parsed from its text; anything else is
 * refused. Clearing the cell is the one exception.
 */

type JsonCellEdit = { ok: true; value: unknown } | { ok: false };

const isStructured = (raw: unknown): raw is object => typeof raw === "object" && raw !== null;

export const jsonCellText = (raw: unknown): string => {
  if (isStructured(raw)) {
    return JSON.stringify(raw);
  }
  return String(raw ?? "");
};

// Comparing against the displayed text, not `String(raw)`, keeps an untouched structure verbatim.
export const resolveJsonCellEdit = (raw: unknown, text: string): JsonCellEdit => {
  if (text === jsonCellText(raw)) {
    return { ok: true, value: raw };
  }
  if (!isStructured(raw)) {
    return { ok: true, value: text };
  }
  // A cleared cell reads as empty to the importer, as it does for every other column.
  if (text.trim() === "") {
    return { ok: true, value: "" };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false };
  }
  return isStructured(parsed) ? { ok: true, value: parsed } : { ok: false };
};
